/* eslint-disable no-unused-vars */
import { FormControl, MenuItem, Select } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Content } from "@iventis/translations/content/typed-content";
import { DataField } from "@iventis/domain-model/model/dataField";
import { DataFieldType } from "@iventis/domain-model/model/dataFieldType";
import { VenueLocation } from "@iventis/domain-model/model/venueLocation";
import { FormWizardTemplate, Stages } from "@iventis/components";
import { EditVenueLocations, VenueLocationsService } from "@iventis/schedule-config";
import { ScheduleConfigVenueLocationType } from "@iventis/domain-model/model/scheduleConfigVenueLocationType";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { IdentifyableEntity } from "@iventis/domain-model/model/identifyableEntity";
import { sectionalMargin, styled } from "@iventis/styles";
import { DataFieldConfigContext, DataFieldListItemTable, DataFieldListItemTableService, DataFieldListItemTableServiceContext } from "@iventis/datafield-editor";
import { DataFieldListItem } from "@iventis/domain-model/model/dataFieldListItem";
import { getDataFieldListItems } from "@iventis/utilities";
import { MicroService } from "@iventis/api-helpers";
import { getApiBaseUrl, scheduleApi } from "@iventis/api/src/api";
import { toastError } from "@iventis/data-table/helpers/data-table-toasts";
import { IventisTableData } from "@iventis/data-table";
import { translate } from "@iventis/translations/translation";
import { createVenueLocation, deleteVenueLocation, editVenueLocation, getVenueLocations } from "./schedule-api-functions";

const venueLocationKey = "VENUE_LOCATION" as const;

interface DataOption<T extends IdentifyableEntity> {
    name: string;
    id: string;
    payload?: T;
}

export const EditScheduleDataOptions: React.FC<{
    close: () => void;
    scheduleId: string;
    dataFields: DataField[];
    venueLocationTypes: ScheduleConfigVenueLocationType[];
    onDismount: (hasDataChanged: boolean) => void;
}> = ({ close, dataFields, scheduleId, venueLocationTypes, onDismount }) => {
    const translate = useIventisTranslate();

    const venueOption = useMemo(() => ({ id: venueLocationKey, name: translate(Content.schedule.add_data_fields_form.venue_location) }), [translate]);

    const isDataChanged = useRef(false);

    useEffect(() => () => onDismount(isDataChanged.current), []);

    // Generate menu options
    const dataOptions = useMemo(() => {
        const dataFieldOptions = dataFields
            // Only lists and Multiselect datafields are shown
            .filter((dataField) => [DataFieldType.List, DataFieldType.MultiSelect].includes(dataField.type))
            .map((dataField) => ({
                payload: dataField,
                name: dataField.name,
                id: dataField.id,
            })) as DataOption<DataField>[];
        return [...dataFieldOptions, venueOption];
    }, [dataFields, venueOption]);

    const [selectedDataOption, setSelectedDataOption] = useState<DataOption<VenueLocation | DataField>>(venueOption);

    const venuesSelected = () => {
        setSelectedDataOption(venueOption);
    };

    const dataFieldSelected = (dataFieldId: string) => {
        const dataField = dataOptions.find((o) => o.id === dataFieldId);
        setSelectedDataOption(dataField);
    };

    const venuesService: VenueLocationsService = useMemo(
        () => ({
            getVenueLocations: () => getVenueLocations(scheduleId),
            updateVenueLocation: (venueLocation) => {
                isDataChanged.current = true;
                return editVenueLocation(venueLocation);
            },
            createVenueLocation: (venueLocation) => {
                isDataChanged.current = true;
                return createVenueLocation({ ...venueLocation, scheduleId });
            },
            deleteVenueLocation: (venueLocation) => {
                isDataChanged.current = true;
                return deleteVenueLocation(venueLocation.id);
            },
        }),
        [scheduleId]
    );

    const Stages: Stages = [
        {
            primaryButtonText: translate(Content.common.buttons.finish),
            primaryButtonCallback: () => close(),
            secondaryButtons: [{ buttonText: translate(Content.common.buttons.close), onButtonPressed: () => close() }],
            isValid: true,
        },
    ];

    const validateListItemRow = (dataField: DataField, data: IventisTableData): boolean => {
        // Ensure user sets a customer id before allowing the row to save
        const customerId = dataField.customerIdentifierName;
        if (customerId != null || customerId !== "") {
            const customerIdentifier = data[customerId];
            if (customerIdentifier == null || customerIdentifier === "") {
                return false;
            }
        }
        return true;
    };

    return (
        <FormWizardTemplate title={translate(Content.schedule.edit_schedule_data_options.title)} stages={Stages} currentStage={0}>
            <FormContainer>
                <FormControl>
                    <Select
                        value={selectedDataOption.id}
                        style={{ marginBottom: sectionalMargin }}
                        onChange={({ target: { value: optionId } }) => (optionId === venueLocationKey ? venuesSelected() : dataFieldSelected(optionId))}
                    >
                        {dataOptions.map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                                {option.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {selectedDataOption.id === venueLocationKey ? (
                    <EditVenueLocations venueService={venuesService} venueLocationTypes={venueLocationTypes} dataFields={dataFields} />
                ) : (
                    <DataFieldConfigContext.Provider value={{ validateListItem: validateListItemRow, allowSetDefaultValue: false, resourceId: undefined }}>
                        <DataFieldListItemTableServiceContext.Provider value={services}>
                            <DataFieldListItemTable
                                selectedDataField={selectedDataOption.payload as DataField}
                                isDataChangedRef={isDataChanged}
                                onNetworkRequestError={(params, refreshTable) => toastError(params, refreshTable)}
                                allowDataDownload
                                allowDataUpload
                                disabled={false}
                            />
                        </DataFieldListItemTableServiceContext.Provider>
                    </DataFieldConfigContext.Provider>
                )}
            </FormContainer>
        </FormWizardTemplate>
    );
};

const services: DataFieldListItemTableService = {
    async getListItems(datafieldId: string): Promise<DataFieldListItem[]> {
        const updatedListItems = await getDataFieldListItems(scheduleApi, datafieldId, []);
        return updatedListItems;
    },
    async getListItem(datafieldId: string, listItemId: string): Promise<DataFieldListItem> {
        const updatedListItem = (await getDataFieldListItems(scheduleApi, datafieldId, [])).find((li) => li.id === listItemId);
        return updatedListItem;
    },
    async deleteListItem(datafieldId: string, listItemId: string): Promise<void> {
        await scheduleApi.delete<DataFieldListItem>(`data-field-list-items/${datafieldId}/${listItemId}`, {
            headers: {
                "Content-Type": "application/json",
            },
            customErrorMessage: {
                title: translate(Content.errors.custom_network_error.delete_error.title, { entity: translate(Content.map.data_fields.list_item) }),
                message: translate(Content.errors.custom_network_error.delete_error.message, { entity: translate(Content.map.data_fields.list_item) }),
            },
        });
    },
    async deleteListItems(datafieldId: string): Promise<void> {
        await scheduleApi.delete(`data-field-list-items/${datafieldId}`, {
            customErrorMessage: {
                title: translate(Content.errors.custom_network_error.delete_error.title, { entity: translate(Content.map.data_fields.list_items) }),
                message: translate(Content.errors.custom_network_error.delete_error.message, { entity: translate(Content.map.data_fields.list_items) }),
            },
        });
    },
    async updateListItem(datafieldId: string, listItem: DataFieldListItem): Promise<DataFieldListItem> {
        await scheduleApi.patch<DataFieldListItem>(`data-field-list-items/${datafieldId}`, listItem, {
            headers: {
                "Content-Type": "application/json",
            },
            customErrorMessage: {
                title: translate(Content.errors.custom_network_error.update.title, { entity: translate(Content.map.data_fields.list_item) }),
                message: translate(Content.errors.custom_network_error.update.message, { entity: translate(Content.map.data_fields.list_item) }),
            },
        });
        return listItem;
    },
    async addListItem(datafieldId: string, listItem: DataFieldListItem): Promise<DataFieldListItem> {
        const response = await scheduleApi.post<DataFieldListItem>(`data-field-list-items/${datafieldId}`, listItem, {
            headers: {
                "Content-Type": "application/json",
            },
            customErrorMessage: {
                title: translate(Content.errors.custom_network_error.create.title, { entity: translate(Content.map.data_fields.list_item) }),
                message: translate(Content.errors.custom_network_error.create.message, { entity: translate(Content.map.data_fields.list_item) }),
            },
        });
        return response.data;
    },
    async importListItems(datafieldId: string, csvJson: string): Promise<void> {
        await scheduleApi.put<DataFieldListItem[]>(`data-field-list-items/${datafieldId}`, csvJson, {
            headers: {
                "Content-Type": "application/json",
            },
            customErrorMessage: {
                title: translate(Content.errors.custom_network_error.import_error.title, { entity: translate(Content.map.data_fields.list_items) }),
                message: translate(Content.errors.custom_network_error.import_error.message, { entity: translate(Content.map.data_fields.list_items) }),
            },
        });
    },
    exportListItems(datafieldId: string): void {
        const baseUrl = getApiBaseUrl(MicroService.SCHEDULE);
        const exportUrl = `${baseUrl}data-field-list-items/${datafieldId}/download`;
        window.open(exportUrl);
    },
    getDataFieldsForResource(): Promise<DataField[]> {
        return Promise.resolve([]); // Schedule does not support relationships so no need to implement this.
    },
};

const FormContainer = styled.div`
    display: flex;
    flex-direction: column;
    row-gap: 10px;
    height: 100%;
    box-sizing: border-box;
    flex-grow: 1;

    .full-width {
        width: 100%;
    }

    .ag-grid-iventis {
        height: auto;
        display: flex;
        // max width of 100% - width of the add column button
        max-width: calc(100% - 45px);
    }
`;
